import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@roger/ui-components';
import {
  getEpisodeTakeOverUndoCheck,
  undoEpisodeTakeOver,
} from '../../services/api';
import type {
  EpisodeSummaryResponse,
  EpisodeTakeoverUndoCheck,
  TakeoverNoaState,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { NOA_STATUS_LABELS } from '../worklog/noaWorkLog';
import {
  UNDO_BACK_LINE,
  UNDO_BLOCKED_TITLE,
  UNDO_CONFIRM,
  UNDO_FAILED_TOAST,
  UNDO_HAZARD_LINE,
  UNDO_LOAD_FAILED_LINE,
  UNDO_NOT_TAKE_OVER_LINE,
  UNDO_REASON_LABEL,
  UNDO_SUCCESS_TOAST,
  UNDO_TITLE,
  undoCycleRange,
  undoCyclesLine,
  undoNoaLine,
} from './takeOverCopy';

/** The server caps the reason at 1000 characters. */
const REASON_MAX = 1000;

type View =
  | { kind: 'loading' }
  | { kind: 'loadFailed' }
  | { kind: 'notTakeOver'; message: string }
  | { kind: 'allowed'; check: EpisodeTakeoverUndoCheck }
  | { kind: 'blocked'; details: string[] };

/** How the NOA screens name the state undo puts the notice back to. */
function noaRestoredLabel(state: TakeoverNoaState): string {
  const label = NOA_STATUS_LABELS[state.status] ?? state.status;
  return state.accepted_externally
    ? `${label}, filed in the External EMR`
    : label;
}

/** The server's 400 sentence (each is written for the office), or ours. */
function notTakeOver(result: unknown): View {
  return {
    kind: 'notTakeOver',
    message: getBusinessErrorMessage(result) ?? UNDO_NOT_TAKE_OVER_LINE,
  };
}

function viewFromCheck(check: EpisodeTakeoverUndoCheck): View {
  if (check.can_undo) return { kind: 'allowed', check };
  return { kind: 'blocked', details: check.blocked_by.map(b => b.detail) };
}

export interface UndoTakeOverDialogProps {
  open: boolean;
  onClose: () => void;
  episodeId: string;
  /** Called with the period as the server returns it after the undo. */
  onUndone: (episode: EpisodeSummaryResponse) => void;
}

/**
 * Undo Take Over (Take Over v2 WP-U2). Reads the server's undo check on open:
 * either what undo does, with a required reason, or every reason it is
 * refused. The server re-runs the same check on submit.
 */
export default function UndoTakeOverDialog({
  open,
  onClose,
  episodeId,
  onUndone,
}: UndoTakeOverDialogProps) {
  const { success: showSuccess, error: showError } = useToast();
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Drops answers that arrive after the dialog closed or reopened.
  const generation = useRef(0);

  const readCheck = useCallback(
    async (gen: number): Promise<View | null> => {
      const result = await getEpisodeTakeOverUndoCheck({
        path: { episode_id: episodeId },
        throwOnError: false,
      });
      if (gen !== generation.current) return null;
      if (result.data) return viewFromCheck(result.data);
      console.error('Failed to read the Undo Take Over check:', result.error);
      if (getHttpStatus(result) === 400) return notTakeOver(result);
      return { kind: 'loadFailed' };
    },
    [episodeId]
  );

  useEffect(() => {
    if (!open) return;
    const gen = ++generation.current;
    setView({ kind: 'loading' });
    setReason('');
    setSubmitting(false);
    void readCheck(gen).then(next => {
      if (next) setView(next);
    });
    return () => {
      generation.current += 1;
    };
  }, [open, readCheck]);

  const trimmedReason = reason.trim();

  const handleConfirm = async () => {
    if (!trimmedReason || submitting) return;
    setSubmitting(true);
    const gen = generation.current;
    try {
      const result = await undoEpisodeTakeOver({
        path: { episode_id: episodeId },
        body: { reason: trimmedReason },
        throwOnError: false,
      });
      if (result.data) {
        // The server committed the undo. The page must learn of it even when
        // this dialog closed or reopened while the request was in flight.
        onUndone(result.data.episode);
        if (gen !== generation.current) return;
        onClose();
        showSuccess(UNDO_SUCCESS_TOAST);
        return;
      }
      if (gen !== generation.current) return;
      console.error('Undo Take Over was refused:', result.error);
      const status = getHttpStatus(result);
      if (status === 409) {
        // Something changed since the check. Show the fresh check, allowed
        // or refused; the 409's own sentence only when the re-read fails.
        const refused = getBusinessErrorMessage(result);
        const next = await readCheck(gen);
        if (gen !== generation.current) return;
        if (next && next.kind !== 'loadFailed') {
          setView(next);
        } else {
          setView({ kind: 'blocked', details: refused ? [refused] : [] });
        }
        return;
      }
      if (status === 400) {
        setView(notTakeOver(result));
        return;
      }
      showError(UNDO_FAILED_TOAST);
    } finally {
      if (gen === generation.current) setSubmitting(false);
    }
  };

  const title =
    view.kind === 'blocked' || view.kind === 'notTakeOver'
      ? UNDO_BLOCKED_TITLE
      : UNDO_TITLE;

  const renderBody = () => {
    switch (view.kind) {
      case 'loading':
        return (
          <p className='text-sm text-muted-foreground' role='status'>
            Checking…
          </p>
        );
      case 'loadFailed':
        return (
          <p
            className='text-sm text-destructive'
            data-testid='undo-takeover-load-error'
          >
            {UNDO_LOAD_FAILED_LINE}
          </p>
        );
      case 'notTakeOver':
        return (
          <p className='text-sm' data-testid='undo-takeover-not-take-over'>
            {view.message}
          </p>
        );
      case 'blocked':
        return (
          <ul className='space-y-1 text-sm' data-testid='undo-takeover-blocked'>
            {view.details.map((detail, i) => (
              <li key={`${i}-${detail}`}>{detail}</li>
            ))}
          </ul>
        );
      case 'allowed': {
        const { check } = view;
        return (
          <div className='space-y-3 text-sm' data-testid='undo-takeover-plan'>
            <p>{UNDO_BACK_LINE}</p>
            <p>{undoCyclesLine(check.cycles_to_remove.length)}</p>
            {check.cycles_to_remove.length > 0 && (
              <ul className='space-y-0.5' data-testid='undo-takeover-cycles'>
                {check.cycles_to_remove.map(cycle => (
                  <li key={cycle.billing_cycle_id}>
                    {undoCycleRange(
                      formatDateForDisplay(cycle.start_date),
                      formatDateForDisplay(cycle.end_date)
                    )}
                  </li>
                ))}
              </ul>
            )}
            {check.noa_restore && (
              <p data-testid='undo-takeover-noa'>
                {undoNoaLine(noaRestoredLabel(check.noa_restore.restored))}
              </p>
            )}
            <p className='font-medium'>{UNDO_HAZARD_LINE}</p>
            <div className='space-y-1'>
              <Label htmlFor='undo-takeover-reason'>{UNDO_REASON_LABEL}</Label>
              <Textarea
                id='undo-takeover-reason'
                value={reason}
                maxLength={REASON_MAX}
                required
                onChange={e => setReason(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
        );
      }
    }
  };

  const renderFooter = () => {
    if (view.kind === 'allowed') {
      return (
        <>
          <Button variant='outline' onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant='destructive'
            onClick={() => void handleConfirm()}
            disabled={submitting || !trimmedReason}
          >
            {submitting ? 'Undoing…' : UNDO_CONFIRM}
          </Button>
        </>
      );
    }
    return (
      <Button variant='outline' onClick={onClose}>
        Close
      </Button>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && !submitting && onClose()}
    >
      <DialogContent
        className='max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-lg'
        data-testid='undo-take-over-dialog'
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className='sr-only'>
            {UNDO_TITLE}
          </DialogDescription>
        </DialogHeader>
        {renderBody()}
        <DialogFooter className='flex-col gap-2 sm:flex-row'>
          {renderFooter()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
